import type { TranslationKey } from '@/lib/core/i18n/types';
import { mewsUnitLabelKey } from '@/services/platform/data/booking/mews';

/**
 * Vocabulary of one Mews booking flow.
 *
 * The sunbed, stay and experience flows book different things through screens that are otherwise
 * identical — the `stay` flow was a 1400-line fork of `sunbed` whose only real differences were
 * words. Rather than a copy per flow, or one prop per string, each flow hands over its own bundle
 * of i18n keys and the shared screens read from it.
 *
 * Values are `TranslationKey`, not `string`, so a key that does not exist in the locale files fails
 * the type-check instead of surfacing as raw `App.Labels.Whatever` in the UI.
 *
 * Grows as more screens are folded back together: `rate.tsx` and `details.tsx` still read their own
 * keys directly, and their entries belong here when they are merged.
 */
export interface MewsFlowLabels {
  /** ICU plural naming one bookable unit — a sunbed, a room, a massage. Call with `{ count }`. */
  unit: TranslationKey;
  /* sheets/which.tsx */
  whichTitle: TranslationKey;
  whichSubtitle: TranslationKey;
  categoriesAvailable: TranslationKey;
  availableUnits: TranslationKey;
  maxPersons: TranslationKey;
  showRate: TranslationKey;
  viewMoreDetails: TranslationKey;
  priceUnit: TranslationKey;
  /* sheets/howMany.tsx */
  adults: TranslationKey;
  adultsAges: TranslationKey;
  children: TranslationKey;
  childrenAges: TranslationKey;
  guestsSectionLabel: TranslationKey;
  /* confirmation.tsx */
  bookingConfirmed: TranslationKey;
  addOn: TranslationKey;
  addOns: TranslationKey;
  /* details.tsx */
  editBooking: TranslationKey;
  guestBreakdown: TranslationKey;
  promotionCode: TranslationKey;
  promotionCodePlaceholder: TranslationKey;
  specialRequestPlaceholder: TranslationKey;
  specialRequests: TranslationKey;
  summaryAddOns: TranslationKey;
  summaryGuestsAdults: TranslationKey;
  summaryGuestsChildren: TranslationKey;
  summaryHowMany: TranslationKey;
  summaryWhen: TranslationKey;
  summaryWhere: TranslationKey;
  summaryWhichCategory: TranslationKey;
  viewBookingSummary: TranslationKey;
  /* rate.tsx */
  rateSubtitle: TranslationKey;
  categoryAvailabilityWarning: TranslationKey;
  guestDistributionTitle: TranslationKey;
  /**
   * Split "N adults / N children missing" wording. Flows that set all three get them instead of
   * `SharedUnitLabels.guestsShortfall`, which is the combined fallback.
   */
  guestsShortfallAdults?: TranslationKey;
  guestsShortfallChildren?: TranslationKey;
  guestsShortfallBoth?: TranslationKey;
  /* book.tsx — "2 Adults, 1 Child" on the How many row */
  adultsCount: TranslationKey;
  childrenCount: TranslationKey;
}

export const SUNBED_LABELS: MewsFlowLabels = {
  unit: 'App.Labels.BookingUnitSunbed',
  whichTitle: 'App.Labels.SunbedWhichTitle',
  whichSubtitle: 'App.Labels.SunbedWhichSubtitle',
  categoriesAvailable: 'App.Labels.SunbedCategoriesAvailableLabel',
  availableUnits: 'App.Labels.SunbedAvailableUnits',
  maxPersons: 'App.Labels.SunbedMaxPersons',
  showRate: 'App.Labels.SunbedShowRate',
  viewMoreDetails: 'App.Labels.SunbedViewMoreDetails',
  priceUnit: 'App.Labels.SunbedPriceUnit',
  adults: 'App.Labels.SunbedAdults',
  adultsAges: 'App.Labels.SunbedAdultsAges',
  children: 'App.Labels.SunbedChildren',
  childrenAges: 'App.Labels.SunbedChildrenAges',
  guestsSectionLabel: 'App.Labels.SunbedGuestsSectionLabel',
  bookingConfirmed: 'App.Labels.SunbedBookingConfirmed',
  addOn: 'App.Labels.SunbedAddOn',
  addOns: 'App.Labels.SunbedAddOns',
  editBooking: 'App.Labels.SunbedEditBooking',
  guestBreakdown: 'App.Labels.SunbedGuestBreakdown',
  promotionCode: 'App.Labels.SunbedPromotionCode',
  promotionCodePlaceholder: 'App.Labels.SunbedPromotionCodePlaceholder',
  specialRequestPlaceholder: 'App.Labels.SunbedSpecialRequestPlaceholder',
  specialRequests: 'App.Labels.SunbedSpecialRequests',
  summaryAddOns: 'App.Labels.SunbedSummaryAddOns',
  summaryGuestsAdults: 'App.Labels.SunbedSummaryGuestsAdults',
  summaryGuestsChildren: 'App.Labels.SunbedSummaryGuestsChildren',
  summaryHowMany: 'App.Labels.SunbedSummaryHowMany',
  summaryWhen: 'App.Labels.SunbedSummaryWhen',
  summaryWhere: 'App.Labels.SunbedSummaryWhere',
  summaryWhichCategory: 'App.Labels.SunbedSummaryWhichCategory',
  viewBookingSummary: 'App.Labels.SunbedViewBookingSummary',
  rateSubtitle: 'App.Labels.SunbedRateSubtitle',
  categoryAvailabilityWarning: 'App.Labels.SunbedCategoryAvailabilityWarning',
  guestDistributionTitle: 'App.Labels.SunbedGuestDistributionTitle',
  adultsCount: 'App.Labels.SunbedAdultsCount',
  childrenCount: 'App.Labels.SunbedChildrenCount',
};

export const STAY_LABELS: MewsFlowLabels = {
  unit: 'App.Labels.BookingUnitRoom',
  whichTitle: 'App.Labels.StayWhichTitle',
  whichSubtitle: 'App.Labels.StayWhichSubtitle',
  categoriesAvailable: 'App.Labels.StayCategoriesAvailableLabel',
  availableUnits: 'App.Labels.StayAvailableUnits',
  maxPersons: 'App.Labels.StayMaxPersons',
  showRate: 'App.Labels.StayShowRate',
  viewMoreDetails: 'App.Labels.StayViewMoreDetails',
  priceUnit: 'App.Labels.StayPriceUnit',
  adults: 'App.Labels.StayAdults',
  adultsAges: 'App.Labels.StayAdultsAges',
  children: 'App.Labels.StayChildren',
  childrenAges: 'App.Labels.StayChildrenAges',
  guestsSectionLabel: 'App.Labels.StayGuestsSectionLabel',
  bookingConfirmed: 'App.Labels.StayBookingConfirmed',
  addOn: 'App.Labels.StayAddOn',
  addOns: 'App.Labels.StayAddOns',
  editBooking: 'App.Labels.StayEditBooking',
  guestBreakdown: 'App.Labels.StayGuestBreakdown',
  promotionCode: 'App.Labels.StayPromotionCode',
  promotionCodePlaceholder: 'App.Labels.StayPromotionCodePlaceholder',
  specialRequestPlaceholder: 'App.Labels.StaySpecialRequestPlaceholder',
  specialRequests: 'App.Labels.StaySpecialRequests',
  summaryAddOns: 'App.Labels.StaySummaryAddOns',
  summaryGuestsAdults: 'App.Labels.StaySummaryGuestsAdults',
  summaryGuestsChildren: 'App.Labels.StaySummaryGuestsChildren',
  summaryHowMany: 'App.Labels.StaySummaryHowMany',
  summaryWhen: 'App.Labels.StaySummaryWhen',
  summaryWhere: 'App.Labels.StaySummaryWhere',
  summaryWhichCategory: 'App.Labels.StaySummaryWhichCategory',
  viewBookingSummary: 'App.Labels.StayViewBookingSummary',
  rateSubtitle: 'App.Labels.StayRateSubtitle',
  categoryAvailabilityWarning: 'App.Labels.StayCategoryAvailabilityWarning',
  guestDistributionTitle: 'App.Labels.StayGuestDistributionTitle',
  adultsCount: 'App.Labels.StayAdultsCount',
  childrenCount: 'App.Labels.StayChildrenCount',
  guestsShortfallAdults: 'App.Labels.StayGuestsShortfallWarningAdults',
  guestsShortfallChildren: 'App.Labels.StayGuestsShortfallWarningChildren',
  guestsShortfallBoth: 'App.Labels.StayGuestsShortfallWarningBoth',
};

/**
 * The experience flow serves several service types through one set of screens, so its unit noun is
 * resolved per service rather than fixed. Every sentence that names the unit now reads it back out
 * of `unit` via `SharedUnitLabels`, so a massage booking no longer inherits the sunbed wording; the
 * rest of the bundle is the sunbed copy, which is what these screens have always shown here.
 */
/**
 * Wording that names the bookable unit, and therefore no longer varies by flow: each string
 * interpolates `{unit}` from `MewsFlowLabels.unit`, so one sentence serves a sunbed, a room and a
 * massage alike. Kept out of the per-flow bundles because a flow overriding one could only restate
 * the same sentence with the noun spelled out — which is how "Remove sunbed?" ended up on a massage.
 *
 * Call sites pass the noun already resolved: `{ unit: t(labels.unit, { count }) }`, singular or
 * plural to suit the sentence.
 */
export const SharedUnitLabels = {
  amountOfUnits: 'App.Labels.BookingAmountOfUnits',
  addAnotherCategory: 'App.Labels.BookingAddAnotherCategory',
  removeUnitTitle: 'App.Labels.BookingRemoveUnitTitle',
  removeUnitDescription: 'App.Labels.BookingRemoveUnitDescription',
  /** Combined "N guests missing". Overridden by a flow's three split keys when it sets them. */
  guestsShortfall: 'App.Labels.BookingGuestsShortfallWarning',
  /**
   * Takes no `{unit}`, deliberately: Portuguese participles agree in gender — "massagem removida"
   * but "quarto removido" — so a placeholder here would render "campo removida". A fixed neutral
   * subject is the only form correct for every service type.
   */
  unitRemovedToast: 'App.Labels.BookingUnitRemovedToast',
} as const satisfies Record<string, TranslationKey>;

export function experienceLabels(serviceType?: string | null): MewsFlowLabels {
  return { ...SUNBED_LABELS, unit: mewsUnitLabelKey(serviceType) };
}
